/**
 * THE EXAM DOCUMENT STORE — a generated exam's typst source, split into a JSON structure a UI can read and
 * write without knowing typst syntax.
 *
 * `encodeExamDocument` walks the source once and produces an ordered list of nodes: plain layout the store
 * does not understand stays a `raw` node, verbatim; a call to one of this template's own content-bearing
 * functions (`#task(...)`, `#material(...)`, and the opening `#show: exam.with(...)` header) becomes a
 * `call` node whose named arguments and trailing content block are separately addressable. `decodeExamDocument`
 * is pure concatenation — every node already carries its own exact source text, so a document with no calls
 * touched decodes back byte-for-byte identical to what was encoded. Editing goes through `setArg`/`setBody`,
 * which splice only the one node touched; everything else in the document is never re-derived, so it cannot
 * drift from what the teacher (or the creation agent) actually wrote — the same guarantee compile-exam.ts
 * relies on elsewhere in this feature: the document is the one source of truth.
 *
 * ONLY THIS TEMPLATE'S OWN DIALECT IS TRACKED (see TRACKED_CALLS), not typst generally. Everything CONTRACT
 * rule 8 already forbids redefining — arbitrary layout via #grid, #align, #block, and so on — is exactly the
 * free-form part a teacher is not meant to hand-edit field by field; it round-trips as opaque raw text
 * instead of being mis-parsed against typst's fully general call grammar (multiple content-block arguments,
 * method chains, arbitrary expressions) that this parser does not attempt to support.
 *
 * NESTING IS NOT FOLLOWED AUTOMATICALLY — one call to `encodeExamDocument` only ever splits one level. A
 * call's trailing content block is captured whole, as opaque text, unless something re-scans it on purpose:
 * pass `NESTED_TASK_CALLS` to a second, scoped call against a `task` node's own `body` to split OUT a
 * subtask, an `#mc`, or a `#figure-block` nested directly inside it — the editor does this recursively
 * (a subtask's own body gets the same treatment) to build hitboxes down to the granularity typst actually
 * gives it geometry for. `#figure-block`'s `callouts:` (a labelling figure's leader-line points) goes one
 * level deeper still, addressed once that node itself has been split out — see `parseCallouts`/`formatCallouts`.
 * A `#formula` or other untracked call stays part of its enclosing body text, same as ever.
 *
 * ASSUMES ALREADY-VALID TYPST. `compileAndPersistExam` never stores a source that failed to compile, so by
 * the time this module ever sees `exam.examSource` its brackets and strings are well-formed by construction
 * — the matcher below trusts that rather than recovering from malformed input.
 */

/** The template's own functions a teacher edits field by field — see the module doc for why the list stops here. */
const TOP_LEVEL_CALLS: ReadonlySet<string> = new Set(['exam', 'task', 'material']);

/**
 * What's worth reaching into a task's own body for: a nested subtask (itself a `task` call, split out the
 * same recursive way again), a multiple-choice grid, or a labelling figure — pass this to a second,
 * scoped `encodeExamDocument(body, NESTED_TASK_CALLS)` call against a `task` node's own `body`. See the
 * module doc's "NESTING IS NOT FOLLOWED AUTOMATICALLY" note for why nothing else in a body is parsed this way.
 */
export const NESTED_TASK_CALLS: ReadonlySet<string> = new Set(['task', 'mc', 'figure-block', 'layout-grid']);

export interface ExamDocumentRawNode {
  type: 'raw';
  text: string;
}

/** One named argument's location within its call's `headSource` — enough to read or splice just this value. */
export interface ExamDocumentArg {
  key: string;
  keyStart: number;
  valueStart: number;
  valueEnd: number;
}

export interface ExamDocumentCallNode {
  type: 'call';
  /** `task`, `material`, or `exam` for the `#show: exam.with(...)` header (there is no other show rule). */
  name: string;
  /** The exact source from `#` (or `#show:` for the header) through the call's closing `)`, byte for byte. */
  headSource: string;
  args: ExamDocumentArg[];
  /** The exact source between the call's trailing `[` and its matching `]`, or null if it takes no content block. */
  body: string | null;
}

export type ExamDocumentNode = ExamDocumentRawNode | ExamDocumentCallNode;

/** The store itself — a plain, JSON-serializable array; nodes are addressed by their index in it. */
export type ExamDocument = ExamDocumentNode[];

// ─── scanning primitives ────────────────────────────────────────────────────────────────────────────

// eslint-disable-next-line require-unicode-regexp -- the 'v' flag needs an es2024 target; project is es2020
const WHITESPACE_PATTERN = /\s/u;
// eslint-disable-next-line require-unicode-regexp -- see above
const WHITESPACE_OR_COMMA_PATTERN = /[\s,]/u;

/**
 * If a `\`-escape, string literal, backtick raw span, or comment starts at `index`, the index just past
 * it — so the scanners below can step over `)`/`]`/`#` characters that appear inside one without
 * miscounting them. The `\` case has to come first and apply everywhere, not just inside strings: typst's
 * markup escape (`\[`, `\]`, `\#`, `\\`, …) is exactly how a teacher's text prints one of these characters
 * literally, and by the time this module sees a source it is ALREADY-VALID typst (see the module doc), so
 * a bare `\` outside a string only ever appears meaning "escape the next character" — never as its own
 * token, which is what makes swallowing it unconditionally, everywhere, safe.
 */
function skipAtomicSpan(source: string, index: number): number | null {
  const ch = source.charAt(index);
  if (ch === '\\') return index + 2;
  if (ch === '"') {
    let j = index + 1;
    while (j < source.length && source.charAt(j) !== '"') j += source.charAt(j) === '\\' ? 2 : 1;
    return j + 1;
  }
  if (ch === '`') {
    let j = index + 1;
    while (j < source.length && source.charAt(j) !== '`') j += 1;
    return j + 1;
  }
  if (ch === '/' && source.charAt(index + 1) === '/') {
    let j = index + 2;
    while (j < source.length && source.charAt(j) !== '\n') j += 1;
    return j;
  }
  if (ch === '/' && source.charAt(index + 1) === '*') {
    let j = index + 2;
    while (j < source.length && !(source.charAt(j) === '*' && source.charAt(j + 1) === '/')) j += 1;
    return j + 2;
  }
  return null;
}

/**
 * TYPST'S MARKUP AND CODE MODES DISAGREE ABOUT WHICH BRACKETS NEST. Inside a call's own argument list —
 * CODE — `(`, `[` and `{` all nest: a paren group, a content-block value (`caption: [...]`), a dict/code
 * block. Inside a call's BODY, or a content-block value found while scanning one — MARKUP, plain prose —
 * only `[`/`]` nest; a bare `(` or `)` is an ordinary printed character. Typst never requires escaping a
 * literal paren (unlike `[`/`]`, which always need `\[`/`\]` to print literally, or this parser would
 * mistake them for real nesting same as it already correctly does for `[`/`]`). This distinction is not
 * optional: a task body reading "1) ...", "2) ...", "3) ..." carries five bare, unmatched `)` characters
 * that a single "count every bracket" scanner mistakes for five real closes, returning the WRONG index for
 * the task's own closing bracket and silently reassembling a corrupted document — exactly the shape of bug
 * that surfaced as a typst compile failure two steps removed from its actual cause. `findMatchingClose`
 * is CODE mode (call `parseArgs`/`findArgBoundary` on a `headSource`); `findMarkupClose` is MARKUP mode
 * (call it on a `body`, i.e. from `[` to its matching `]`). Each recurses into the other exactly where
 * typst itself switches modes: `findMatchingClose` hands a `[` value off to `findMarkupClose`;
 * `findMarkupClose` hands any `#name(...)` call — tracked by this module or not, it makes no difference to
 * finding where the CURRENT body ends — off to `skipCallSpan`, which is itself CODE mode for that call's
 * own args (and, recursively, MARKUP mode again for that call's own body, if it takes one).
 */
function findMatchingClose(source: string, openIndex: number): number {
  let depth = 0;
  let i = openIndex;
  while (i < source.length) {
    const skip = skipAtomicSpan(source, i);
    if (skip !== null) {
      i = skip;
      continue;
    }
    const ch = source.charAt(i);
    if (ch === '[') {
      i = findMarkupClose(source, i) + 1;
      continue;
    }
    if (ch === '(' || ch === '{') depth += 1;
    else if (ch === ')' || ch === '}') {
      depth -= 1;
      if (depth === 0) return i;
    }
    i += 1;
  }
  throw new Error(`exam-document: unbalanced bracket from index ${openIndex}`);
}

/** See `findMatchingClose`'s doc comment — the MARKUP-mode half of the same pair. */
function findMarkupClose(source: string, openIndex: number): number {
  let depth = 0;
  let i = openIndex;
  while (i < source.length) {
    const skip = skipAtomicSpan(source, i);
    if (skip !== null) {
      i = skip;
      continue;
    }
    const call = skipCallSpan(source, i);
    if (call !== null) {
      i = call;
      continue;
    }
    const ch = source.charAt(i);
    if (ch === '[') depth += 1;
    else if (ch === ']') {
      depth -= 1;
      if (depth === 0) return i;
    }
    i += 1;
  }
  throw new Error(`exam-document: unbalanced bracket from index ${openIndex}`);
}

/**
 * Skips one whole `#name(...)` call starting at `index` — tracked by this module or not, since here the
 * only thing that matters is not letting its own (CODE-mode) argument brackets leak into the MARKUP scan
 * around it — plus its trailing content-block body, if it has one (that body is MARKUP again, so it is
 * walked by `findMarkupClose` too, recursively). Returns `null` if no call starts at `index`.
 */
function skipCallSpan(source: string, index: number): number | null {
  const rest = source.slice(index);
  const header = HEADER_PATTERN.exec(rest);
  const call = header ? null : CALL_PATTERN.exec(rest);
  if (!header && !call) return null;
  const matchLength = (header ?? call ?? []).at(0)?.length;
  if (!matchLength) return null;
  const parenStart = index + matchLength - 1;
  const closeParen = findMatchingClose(source, parenStart);
  const headEnd = closeParen + 1;
  if (source.charAt(headEnd) !== '[') return headEnd;
  return findMarkupClose(source, headEnd) + 1;
}

interface CallStart {
  index: number;
  name: string;
  /** Index of the args list's opening "(" — for the header this is `.with(`'s paren, not `#show:`'s. */
  parenStart: number;
}

/**
 * Reads a named capture group whose regex guarantees it is present whenever the overall match succeeds —
 * every use here is on a group that is not itself optional, so a missing value means the pattern and this
 * read of it have drifted apart, not a normal "no match" case (that is what the caller already checked for
 * by testing the match itself before calling this).
 */
function requiredGroup(match: RegExpExecArray | RegExpMatchArray, name: string): string {
  const value = match.groups?.[name];
  if (value === undefined) throw new Error(`exam-document: expected capture group "${name}"`);
  return value;
}

// eslint-disable-next-line require-unicode-regexp -- the 'v' flag needs an es2024 target; project is es2020
const HEADER_PATTERN = /^#show:\s*exam\.with\(/;
// eslint-disable-next-line require-unicode-regexp -- see above
const CALL_PATTERN = /^#(?<name>[\w-]+)\(/;

/** The next tracked call at or after `from`, skipping over anything inside a string, raw span or comment. */
function findNextCallStart(source: string, from: number, trackedCalls: ReadonlySet<string>): CallStart | null {
  let i = from;
  while (i < source.length) {
    const skip = skipAtomicSpan(source, i);
    if (skip !== null) {
      i = skip;
      continue;
    }
    if (source.charAt(i) === '#') {
      const rest = source.slice(i);
      const header = HEADER_PATTERN.exec(rest);
      if (header && trackedCalls.has('exam')) return { index: i, name: 'exam', parenStart: i + header[0].length - 1 };
      const call = CALL_PATTERN.exec(rest);
      if (call) {
        const name = requiredGroup(call, 'name');
        if (trackedCalls.has(name)) return { index: i, name, parenStart: i + call[0].length - 1 };
      }
    }
    i += 1;
  }
  return null;
}

// ─── argument parsing ───────────────────────────────────────────────────────────────────────────────

// eslint-disable-next-line require-unicode-regexp -- see HEADER_PATTERN above
const ARG_KEY_PATTERN = /^(?<key>[\w-]+)\s*:\s*/;

/**
 * The index of the next top-level "," at or after `from` (respecting nesting and atomics), or `limit`.
 * CODE mode, same as `findMatchingClose` — a `[` value (an option's own content block, a caption, …) is
 * handed off to `findMarkupClose` for the same reason: a bare `(`/`)` inside it (an MC option reading
 * "(a) is correct", say) is plain text there, not nesting, and must not feed this function's own depth.
 */
function findArgBoundary(source: string, from: number, limit: number): number {
  let depth = 0;
  let i = from;
  while (i < limit) {
    const skip = skipAtomicSpan(source, i);
    if (skip !== null) {
      i = Math.min(skip, limit);
      continue;
    }
    const ch = source.charAt(i);
    if (ch === '[') {
      i = Math.min(findMarkupClose(source, i) + 1, limit);
      continue;
    }
    if (ch === '(' || ch === '{') depth += 1;
    else if (ch === ')' || ch === '}') depth -= 1;
    else if (ch === ',' && depth === 0) return i;
    i += 1;
  }
  return limit;
}

/** `headSource` always ends in the args list's own closing ")" (see CallStart), so args are self-contained. */
function parseArgs(headSource: string): ExamDocumentArg[] {
  const openParen = headSource.indexOf('(');
  const closeParen = headSource.length - 1;
  const args: ExamDocumentArg[] = [];

  let i = openParen + 1;
  while (i < closeParen) {
    while (i < closeParen && WHITESPACE_OR_COMMA_PATTERN.test(headSource.charAt(i))) i += 1;
    if (i >= closeParen) break;

    const keyMatch = ARG_KEY_PATTERN.exec(headSource.slice(i, closeParen));
    const boundary = findArgBoundary(headSource, i, closeParen);
    if (keyMatch) {
      const keyStart = i;
      const valueStart = i + keyMatch[0].length;
      let valueEnd = boundary;
      while (valueEnd > valueStart && WHITESPACE_PATTERN.test(headSource.charAt(valueEnd - 1))) valueEnd -= 1;
      args.push({ key: requiredGroup(keyMatch, 'key'), keyStart, valueStart, valueEnd });
    }
    // A positional argument (no "key:") carries no field a UI could address by name, so it is left out of
    // `args` entirely — it still round-trips, as part of `headSource`, just not individually editable.
    i = boundary + 1;
  }
  return args;
}

// ─── encode / decode ────────────────────────────────────────────────────────────────────────────────

/**
 * `trackedCalls` defaults to this template's top-level content blocks (`task`, `material`, the header) —
 * pass `NESTED_TASK_CALLS` to instead encode a `task` node's own `body` for the subtask/`mc`/`figure-block`
 * calls nested directly inside it.
 */
export function encodeExamDocument(source: string, trackedCalls: ReadonlySet<string> = TOP_LEVEL_CALLS): ExamDocument {
  const nodes: ExamDocument = [];
  let cursor = 0;

  while (cursor < source.length) {
    const next = findNextCallStart(source, cursor, trackedCalls);
    if (!next) {
      nodes.push({ type: 'raw', text: source.slice(cursor) });
      break;
    }
    if (next.index > cursor) nodes.push({ type: 'raw', text: source.slice(cursor, next.index) });

    const closeParen = findMatchingClose(source, next.parenStart);
    const headEnd = closeParen + 1;

    let body: string | null = null;
    let nodeEnd = headEnd;
    if (source.charAt(headEnd) === '[') {
      const closeBracket = findMarkupClose(source, headEnd);
      body = source.slice(headEnd + 1, closeBracket);
      nodeEnd = closeBracket + 1;
    }

    const headSource = source.slice(next.index, headEnd);
    nodes.push({ type: 'call', name: next.name, headSource, args: parseArgs(headSource), body });
    cursor = nodeEnd;
  }

  return nodes;
}

function nodeSource(node: ExamDocumentNode): string {
  if (node.type === 'raw') return node.text;
  if (node.body === null) return node.headSource;
  return `${node.headSource}[${node.body}]`;
}

export function decodeExamDocument(document: ExamDocument): string {
  return document.map(nodeSource).join('');
}

/**
 * The "own" prose of an already-`NESTED_TASK_CALLS`-encoded body — everything before its first nested
 * subtask/mc/figure, trimmed of the trailing blank line that separates it from them. A task's own edit
 * form shows only this, not the nested calls' source: a nested subtask/mc/figure is edited through its
 * own hitbox, and letting its raw typst leak into the parent's textarea (and letting an edit there
 * clobber it) is exactly what recursing into hitboxes down to every nested call was meant to avoid.
 */
export function getOwnText(nested: ExamDocument): string {
  return nested[0]?.type === 'raw' ? nested[0].text.trimEnd() : '';
}

/** The inverse of `getOwnText` — replaces (or inserts) just the leading prose, leaving every nested call untouched. */
export function setOwnText(nested: ExamDocument, text: string): ExamDocument {
  const rest = nested[0]?.type === 'raw' ? nested.slice(1) : nested;
  if (!text) return rest;
  const leading: ExamDocumentRawNode = { type: 'raw', text: rest.length > 0 ? `${text}\n\n` : text };
  return [leading, ...rest];
}

// ─── editing ────────────────────────────────────────────────────────────────────────────────────────

export function getArg(node: ExamDocumentCallNode, key: string): string | undefined {
  const arg = node.args.find(candidate => candidate.key === key);
  return arg ? node.headSource.slice(arg.valueStart, arg.valueEnd) : undefined;
}

/** `rawValue` is typst source for the new value (see `formatTypstString`), not a display string. `null` removes the arg. */
export function setArg(node: ExamDocumentCallNode, key: string, rawValue: string | null): ExamDocumentCallNode {
  const existing = node.args.find(candidate => candidate.key === key);

  if (rawValue === null) {
    if (!existing) return node;
    const headSource = removeArg(node.headSource, node.args, existing);
    return { ...node, headSource, args: parseArgs(headSource) };
  }
  if (existing) {
    const headSource =
      node.headSource.slice(0, existing.valueStart) + rawValue + node.headSource.slice(existing.valueEnd);
    return { ...node, headSource, args: parseArgs(headSource) };
  }
  const headSource = insertArg(node.headSource, key, rawValue, node.args.length > 0);
  return { ...node, headSource, args: parseArgs(headSource) };
}

function insertArg(headSource: string, key: string, rawValue: string, hasExistingArgs: boolean): string {
  const insertion = hasExistingArgs ? `, ${key}: ${rawValue}` : `${key}: ${rawValue}`;
  return `${headSource.slice(0, -1)}${insertion})`;
}

/** Removes "key: value" plus one adjacent separating comma, so neither a double comma nor a dangling one is left. */
function removeArg(headSource: string, args: ExamDocumentArg[], target: ExamDocumentArg): string {
  const index = args.findIndex(candidate => candidate.key === target.key);
  const next = args[index + 1];
  if (next) return headSource.slice(0, target.keyStart) + headSource.slice(next.keyStart);

  let start = target.keyStart;
  let j = start;
  while (j > 0 && WHITESPACE_PATTERN.test(headSource.charAt(j - 1))) j -= 1;
  if (j > 0 && headSource.charAt(j - 1) === ',') start = j - 1;
  return headSource.slice(0, start) + headSource.slice(target.valueEnd);
}

/**
 * A call's one unkeyed argument — e.g. `#mc`'s options tuple in `#mc(id: "..", (...))`, wherever `id` falls
 * relative to it. `parseArgs` already walks past every `key:` argument to find this same span; it just
 * throws the boundary away instead of keeping it, since nothing needed a positional value until `#mc`.
 */
export function getPositionalArg(node: ExamDocumentCallNode): string | undefined {
  const openParen = node.headSource.indexOf('(');
  const closeParen = node.headSource.length - 1;
  let i = openParen + 1;
  while (i < closeParen) {
    while (i < closeParen && WHITESPACE_OR_COMMA_PATTERN.test(node.headSource.charAt(i))) i += 1;
    if (i >= closeParen) break;
    const keyMatch = ARG_KEY_PATTERN.exec(node.headSource.slice(i, closeParen));
    const boundary = findArgBoundary(node.headSource, i, closeParen);
    if (!keyMatch) return node.headSource.slice(i, boundary).trim();
    i = boundary + 1;
  }
  return undefined;
}

/** The inverse of `getPositionalArg` — replaces the same span, or appends one if the call had none yet. */
export function setPositionalArg(node: ExamDocumentCallNode, rawValue: string): ExamDocumentCallNode {
  const openParen = node.headSource.indexOf('(');
  const closeParen = node.headSource.length - 1;
  let i = openParen + 1;
  while (i < closeParen) {
    while (i < closeParen && WHITESPACE_OR_COMMA_PATTERN.test(node.headSource.charAt(i))) i += 1;
    if (i >= closeParen) break;
    const keyMatch = ARG_KEY_PATTERN.exec(node.headSource.slice(i, closeParen));
    const boundary = findArgBoundary(node.headSource, i, closeParen);
    if (!keyMatch) {
      const headSource = node.headSource.slice(0, i) + rawValue + node.headSource.slice(boundary);
      return { ...node, headSource, args: parseArgs(headSource) };
    }
    i = boundary + 1;
  }
  const hasExistingArgs = closeParen > openParen + 1;
  const insertion = hasExistingArgs ? `${rawValue}, ` : rawValue;
  const headSource = `${node.headSource.slice(0, openParen + 1)}${insertion}${node.headSource.slice(openParen + 1)}`;
  return { ...node, headSource, args: parseArgs(headSource) };
}

export function getBody(node: ExamDocumentCallNode): string | null {
  return node.body;
}

export function setBody(node: ExamDocumentCallNode, newBody: string): ExamDocumentCallNode {
  return { ...node, body: newBody };
}

// ─── typst literal helpers ──────────────────────────────────────────────────────────────────────────

// eslint-disable-next-line require-unicode-regexp -- see HEADER_PATTERN above
const BACKSLASH_PATTERN = /\\/gu;
// eslint-disable-next-line require-unicode-regexp -- see HEADER_PATTERN above
const DOUBLE_QUOTE_PATTERN = /"/gu;
// eslint-disable-next-line require-unicode-regexp -- see HEADER_PATTERN above
const QUOTED_STRING_PATTERN = /^"(?<value>[\s\S]*)"$/;
// eslint-disable-next-line require-unicode-regexp -- see HEADER_PATTERN above
const ESCAPED_CHAR_PATTERN = /\\(?<char>.)/gu;

/** A JS string as a typst string literal, for use as a `setArg` value: `formatTypstString('Ms. X')` -> `"Ms. X"`. */
export function formatTypstString(value: string): string {
  return `"${value.replace(BACKSLASH_PATTERN, '\\\\').replace(DOUBLE_QUOTE_PATTERN, '\\"')}"`;
}

/** The inverse of `formatTypstString` — a `getArg` value that is a quoted string literal, unescaped and unquoted. */
export function parseTypstString(raw: string): string {
  const trimmed = raw.trim();
  const match = QUOTED_STRING_PATTERN.exec(trimmed);
  return match ? requiredGroup(match, 'value').replace(ESCAPED_CHAR_PATTERN, '$<char>') : trimmed;
}

/** A list of JS strings as a typst array-of-strings literal, for arguments like `#material`'s `tasks:`. */
export function formatTypstStringArray(values: string[]): string {
  return `(${values.map(formatTypstString).join(', ')})`;
}

// eslint-disable-next-line require-unicode-regexp -- see HEADER_PATTERN above
const LEADING_PAREN_PATTERN = /^\(/u;
// eslint-disable-next-line require-unicode-regexp -- see HEADER_PATTERN above
const TRAILING_PAREN_PATTERN = /\)$/u;

/**
 * Strips one outer "(...)" array wrapper and splits the interior into top-level, comma-separated element
 * substrings — shared by `parseTypstStringArray` (elements are string literals) and `parseCallouts`
 * (elements are their own "(label: .., x: .., y: ..)" records).
 */
function splitTopLevelElements(raw: string): string[] {
  const trimmed = raw.trim().replace(LEADING_PAREN_PATTERN, '').replace(TRAILING_PAREN_PATTERN, '');
  if (!trimmed.trim()) return [];
  const elements: string[] = [];
  let i = 0;
  while (i < trimmed.length) {
    while (i < trimmed.length && WHITESPACE_OR_COMMA_PATTERN.test(trimmed.charAt(i))) i += 1;
    if (i >= trimmed.length) break;
    const boundary = findArgBoundary(trimmed, i, trimmed.length);
    elements.push(trimmed.slice(i, boundary).trim());
    i = boundary + 1;
  }
  return elements;
}

/** The inverse of `formatTypstStringArray` — top-level string-literal elements only; anything else is skipped. */
export function parseTypstStringArray(raw: string): string[] {
  return splitTopLevelElements(raw)
    .filter(element => element.startsWith('"'))
    .map(parseTypstString);
}

// ─── multiple-choice options ────────────────────────────────────────────────────────────────────────

// eslint-disable-next-line require-unicode-regexp -- see HEADER_PATTERN above
const CONTENT_BLOCK_PATTERN = /^\[(?<value>[\s\S]*)\]$/;

/** The inverse of `formatMcOptions` — `#mc`'s options tuple, `( [Option A], [Option B] )`, unwrapped to plain text. */
export function parseMcOptions(raw: string): string[] {
  return splitTopLevelElements(raw)
    .map(element => CONTENT_BLOCK_PATTERN.exec(element))
    .filter((match): match is RegExpExecArray => match !== null)
    .map(match => requiredGroup(match, 'value'));
}

/** A list of option strings as an `#mc` options tuple — each wrapped in its own content block, `[...]`. */
export function formatMcOptions(options: string[]): string {
  return `(${options.map(option => `[${option}]`).join(', ')})`;
}

// ─── figure callouts (leader-line points) ──────────────────────────────────────────────────────────

export interface ExamCallout {
  label: string;
  x: number;
  y: number;
}

/** The inverse of `formatCallouts` — a `#figure-block`'s `callouts:` value, "((label: "..", x: .., y: ..), ...)". */
export function parseCallouts(raw: string): ExamCallout[] {
  const callouts: ExamCallout[] = [];
  for (const tuple of splitTopLevelElements(raw)) {
    if (!tuple.startsWith('(')) continue;
    const args = parseArgs(tuple);
    const label = args.find(arg => arg.key === 'label');
    const x = args.find(arg => arg.key === 'x');
    const y = args.find(arg => arg.key === 'y');
    if (!label || !x || !y) continue;
    callouts.push({
      label: parseTypstString(tuple.slice(label.valueStart, label.valueEnd)),
      x: Number(tuple.slice(x.valueStart, x.valueEnd)),
      y: Number(tuple.slice(y.valueStart, y.valueEnd)),
    });
  }
  return callouts;
}

/** A list of `{label, x, y}` points as a `#figure-block` `callouts:` value — x/y are 0-1, top-left origin. */
export function formatCallouts(callouts: ExamCallout[]): string {
  const tuples = callouts.map(
    callout => `(label: ${formatTypstString(callout.label)}, x: ${callout.x.toFixed(3)}, y: ${callout.y.toFixed(3)})`,
  );
  return `(${tuples.join(', ')})`;
}

// ─── adding a task ──────────────────────────────────────────────────────────────────────────────────

export interface NewTaskFields {
  id: string;
  number: string;
  taskType: string;
  points?: number;
  lines?: number;
  body: string;
}

/** A fresh `task` call node, not derived from any source — for adding a task rather than editing one. */
export function createTaskNode(fields: NewTaskFields): ExamDocumentCallNode {
  const parts = [
    `id: ${formatTypstString(fields.id)}`,
    `number: ${formatTypstString(fields.number)}`,
    `type: ${formatTypstString(fields.taskType)}`,
  ];
  if (fields.points !== undefined) parts.push(`points: ${fields.points}`);
  if (fields.lines !== undefined) parts.push(`lines: ${fields.lines}`);
  const headSource = `#task(${parts.join(', ')})`;
  return { type: 'call', name: 'task', headSource, args: parseArgs(headSource), body: fields.body };
}

/** Appends a node at the end of the document on its own blank line — for adding a task at the end. */
export function appendNode(document: ExamDocument, node: ExamDocumentNode): ExamDocument {
  return [...document, { type: 'raw', text: '\n\n' }, node];
}
